<template>
  <v-dialog
    :value="visible"
    max-width="540"
    persistent
    @input="val => !val && $emit('close')"
  >
    <v-card class="dialog-card">
      <!-- Title bar -->
      <div class="dialog-title-bar">
        <v-icon small class="mr-2" color="blue lighten-1"
          >mdi-content-copy</v-icon
        >
        <span class="subtitle-2">Apply payment plan to other buildings</span>
      </div>
      <v-divider></v-divider>

      <!-- Loading -->
      <v-card-text v-if="loading" class="text-center py-10">
        <v-progress-circular
          indeterminate
          color="blue darken-1"
          size="36"
        ></v-progress-circular>
        <div class="caption text--secondary mt-3">Loading buildings…</div>
      </v-card-text>

      <!-- Applying progress -->
      <v-card-text v-else-if="applying" class="py-8">
        <div class="caption text--secondary mb-4 text-center">
          Applying payment plan…
        </div>
        <v-progress-linear
          :value="(progress.done / progress.total) * 100"
          color="blue darken-1"
          rounded
          height="8"
          class="mb-3"
        ></v-progress-linear>
        <div class="caption text--secondary text-center">
          {{ progress.done }} / {{ progress.total }} buildings updated
        </div>
      </v-card-text>

      <!-- Done -->
      <v-card-text v-else-if="done" class="text-center py-8">
        <div class="done-icon-wrap">
          <v-icon color="green darken-1" size="40"
            >mdi-check-circle-outline</v-icon
          >
        </div>
        <div class="body-2 mt-3">
          Payment plan applied to
          <strong>{{ progress.done }}</strong>
          building{{ progress.done !== 1 ? 's' : '' }}.
        </div>
      </v-card-text>

      <!-- Building selection -->
      <template v-else>
        <v-card-text class="pb-2 pt-4">
          <!-- No eligible buildings -->
          <div
            v-if="!eligibleBuildings.length"
            class="caption text--secondary text-center py-6"
          >
            All other buildings in this project already have a payment plan
            configured.
          </div>

          <template v-else>
            <div class="caption text--secondary mb-4">
              Only buildings without an existing plan are shown.
            </div>

            <!-- Global select-all row -->
            <div class="global-select-row mb-4">
              <v-btn
                small
                :outlined="!allSelected"
                :color="allSelected ? 'blue darken-1' : 'blue darken-1'"
                :text="allSelected"
                class="select-all-btn"
                @click="toggleSelectAll"
              >
                <v-icon small left>{{
                  allSelected
                    ? 'mdi-checkbox-multiple-marked'
                    : 'mdi-checkbox-multiple-blank-outline'
                }}</v-icon>
                {{ allSelected ? 'Deselect all' : 'Select all buildings' }}
              </v-btn>
              <span class="caption text--secondary ml-3">
                {{ selectedBuildingIds.length }} /
                {{ eligibleBuildings.length }} selected
              </span>
            </div>

            <!-- Masterplan groups -->
            <div
              v-for="group in groupedBuildings"
              :key="group.masterplanId"
              class="masterplan-group mb-3"
            >
              <!-- Group header with per-group toggle -->
              <div class="group-header-row">
                <div class="group-header-label">
                  <v-icon x-small class="mr-1 group-icon"
                    >mdi-map-outline</v-icon
                  >
                  {{ group.masterplanName }}
                  <span class="group-count"
                    >({{ group.buildings.length }})</span
                  >
                </div>
                <v-btn
                  x-small
                  text
                  color="blue lighten-2"
                  class="group-toggle-btn"
                  @click="toggleMasterplanGroup(group)"
                >
                  {{
                    isMasterplanGroupSelected(group)
                      ? 'Deselect all'
                      : 'Select all'
                  }}
                </v-btn>
              </div>

              <!-- Building checkboxes -->
              <div class="group-buildings">
                <div
                  v-for="b in group.buildings"
                  :key="b.id"
                  class="building-row"
                  :class="{ 'building-row--selected': selectedBuildingIds.includes(b.id) }"
                  @click="toggleBuilding(b.id)"
                >
                  <v-icon x-small class="building-check-icon mr-2">{{
                    selectedBuildingIds.includes(b.id)
                      ? 'mdi-checkbox-marked'
                      : 'mdi-checkbox-blank-outline'
                  }}</v-icon>
                  <span class="body-2 building-name">{{
                    b.name || b.title
                  }}</span>
                </div>
              </div>
            </div>
          </template>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn text small @click="$emit('close')">Cancel</v-btn>
          <v-btn
            color="blue darken-1"
            small
            :disabled="!selectedBuildingIds.length"
            @click="applyPlan"
          >
            Apply &amp; Save
          </v-btn>
        </v-card-actions>
      </template>

      <!-- Done actions -->
      <template v-if="done">
        <v-divider></v-divider>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" small @click="close">Done</v-btn>
        </v-card-actions>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import BuildingService from '../services/BuildingService'

export default {
  name: 'ApplyPaymentPlanDialog',
  props: {
    visible: { type: Boolean, default: false },
    paymentPlan: { type: Array, default: () => [] },
    currentBuildingId: { type: Number, default: null }
  },
  data() {
    return {
      loading: false,
      applying: false,
      done: false,
      selectedBuildingIds: [],
      progress: { done: 0, total: 0 }
    }
  },
  computed: {
    projectBuildings() {
      return this.$store.state.BuildingStore.projectBuildings || []
    },
    masterplans() {
      return this.$store.state.MasterplanStore.projectMasterplans || []
    },
    eligibleBuildings() {
      return this.projectBuildings.filter(b => {
        if (b.id === this.currentBuildingId) return false
        const plan = b.properties && b.properties.paymentPlan
        return !plan || !plan.length
      })
    },
    groupedBuildings() {
      const map = {}
      this.eligibleBuildings.forEach(b => {
        const mp = this.masterplans.find(m => m.id === b.masterplansId)
        const key = b.masterplansId || 0
        const name = mp ? mp.name : `Masterplan ${key}`
        if (!map[key]) {
          map[key] = { masterplanId: key, masterplanName: name, buildings: [] }
        }
        map[key].buildings.push(b)
      })
      return Object.values(map)
    },
    allSelected() {
      return (
        this.eligibleBuildings.length > 0 &&
        this.selectedBuildingIds.length === this.eligibleBuildings.length
      )
    }
  },
  watch: {
    visible(val) {
      if (val) this.onOpen()
    }
  },
  methods: {
    async onOpen() {
      this.reset()
      this.loading = true
      await this.$store.dispatch('BuildingStore/fetchProjectBuildings')
      this.loading = false
    },
    reset() {
      this.done = false
      this.applying = false
      this.selectedBuildingIds = []
      this.progress = { done: 0, total: 0 }
    },
    close() {
      this.$emit('close')
      this.$nextTick(() => this.reset())
    },
    toggleBuilding(id) {
      const idx = this.selectedBuildingIds.indexOf(id)
      if (idx === -1) {
        this.selectedBuildingIds.push(id)
      } else {
        this.selectedBuildingIds.splice(idx, 1)
      }
    },
    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedBuildingIds = []
      } else {
        this.selectedBuildingIds = this.eligibleBuildings.map(b => b.id)
      }
    },
    isMasterplanGroupSelected(group) {
      return group.buildings.every(b =>
        this.selectedBuildingIds.includes(b.id)
      )
    },
    toggleMasterplanGroup(group) {
      if (this.isMasterplanGroupSelected(group)) {
        const ids = group.buildings.map(b => b.id)
        this.selectedBuildingIds = this.selectedBuildingIds.filter(
          id => !ids.includes(id)
        )
      } else {
        const toAdd = group.buildings
          .map(b => b.id)
          .filter(id => !this.selectedBuildingIds.includes(id))
        this.selectedBuildingIds = [...this.selectedBuildingIds, ...toAdd]
      }
    },
    async applyPlan() {
      this.applying = true
      this.progress = { done: 0, total: this.selectedBuildingIds.length }
      const plan = JSON.parse(JSON.stringify(this.paymentPlan))
      for (const id of this.selectedBuildingIds) {
        try {
          const res = await BuildingService.getBuildingById(id)
          const b = res.data
          if (!b.properties) b.properties = {}
          b.properties.paymentPlan = plan
          await BuildingService.updateBuilding(b)
        } catch (e) {
          // continue with remaining buildings on error
        }
        this.progress.done++
      }
      this.applying = false
      this.done = true
    }
  }
}
</script>

<style scoped>
.dialog-card {
  background: #1e1e1e;
}

.dialog-title-bar {
  display: flex;
  align-items: center;
  padding: 14px 20px;
}

.global-select-row {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: rgba(66, 165, 245, 0.06);
  border-radius: 6px;
  border: 1px solid rgba(66, 165, 245, 0.15);
}

.select-all-btn {
  flex-shrink: 0;
}

.masterplan-group {
  border-left: 2px solid rgba(66, 165, 245, 0.35);
  border-radius: 0 6px 6px 0;
  overflow: hidden;
}

.group-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 12px;
  background: rgba(255, 255, 255, 0.04);
}

.group-header-label {
  display: flex;
  align-items: center;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  opacity: 0.6;
}

.group-icon {
  opacity: 0.7;
}

.group-count {
  margin-left: 5px;
  font-weight: 400;
  opacity: 0.6;
}

.group-toggle-btn {
  font-size: 10px !important;
  letter-spacing: 0.04em;
}

.group-buildings {
  padding: 4px 0;
}

.building-row {
  display: flex;
  align-items: center;
  padding: 7px 12px;
  cursor: pointer;
  transition: background 0.1s ease;
  user-select: none;
}

.building-row:hover {
  background: rgba(255, 255, 255, 0.05);
}

.building-row--selected {
  background: rgba(66, 165, 245, 0.08);
}

.building-row--selected:hover {
  background: rgba(66, 165, 245, 0.12);
}

.building-check-icon {
  opacity: 0.55;
  flex-shrink: 0;
}

.building-row--selected .building-check-icon {
  opacity: 1;
  color: #42a5f5 !important;
}

.building-name {
  font-size: 13px;
  line-height: 1.3;
}

.done-icon-wrap {
  display: inline-flex;
  padding: 12px;
  background: rgba(76, 175, 80, 0.1);
  border-radius: 50%;
}
</style>
